import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Table, Button, Modal, Select, Upload, Switch, message, Card, DatePicker, Spin, Tag, Checkbox, Input, List, Empty } from 'antd';
import { getAuthHeaders } from '../utils/authHeaders';
import { useAuth } from '../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Dayjs } from 'dayjs';
import { PlusOutlined, UploadOutlined, DeleteOutlined, LoadingOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, UserOutlined, ClearOutlined, FileTextOutlined, SettingOutlined } from '@ant-design/icons';
import { FileClock, FileCheck, CircleX, Files, Loader, RotateCcw } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import DemographicsSmartSearch from '../components/DemographicsSmartSearch';
import '../styles/DemographicsPage.css';
const BACKEND_URL = '/api/demographics';

const { Option } = Select;

// Sortable Item Component
interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: (key: string, checked: boolean) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, label, checked, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
        border: isDragging ? '1px solid #d9d9d9' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <div 
        {...attributes}
        {...listeners}
        style={{ 
          marginRight: '8px',
          cursor: 'grab',
          color: '#8c8c8c',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e8e8e8'
        }}
      >
        ⋮⋮
      </div>
      <Checkbox
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(id, e.target.checked);
        }}
        style={{ marginRight: '8px' }}
        onClick={(e) => e.stopPropagation()}
      />
      <span 
        style={{ 
          fontSize: '13px',
          color: '#262626',
          cursor: 'pointer',
          userSelect: 'none',
          flex: 1
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(id, !checked);
        }}
      >
        {label}
      </span>
    </div>
  );
};

// Auth headers are now imported from utils/authHeaders

interface DemographicsData {
  id: string;
  doctorName: string;
  procedureName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  surgeryDate: string;
  uploadedOn: string;
  lastModified: string;
  filename: string;
  contentType: string;
  onDemand: boolean;
  status: 'Pending' | 'In Progress' | 'Accepted' | 'Rejected';
  isDuplicate: boolean;
  rejectedBy?: string;
  rejectedReason?: string;
}

interface DeletedRecord {
  _id: string;
  originalId: number;
  doctorName: string;
  procedureName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  surgeryDate: string;
  uploadedOn: string;
  lastModified: string;
  deletedOn: string;
  filename: string;
  contentType: string;
  onDemand: boolean;
  status: 'Pending' | 'In Progress' | 'Accepted' | 'Rejected';
  isDuplicate: boolean;
  deletionReason?: string;
}

interface Doctor {
  employeeId: string;
  name: string;
  specializations: string[];
}

interface FileUploadState {
  selectedFile: File | null;
  isUploading: boolean;
  uploadStatus: string;
  selectedDoctor: string;
  selectedProcedure: string;
  surgeryDate: any;
  enableOnDemand: boolean;
  uploadError: string | null;
}

interface Note {
  _id: string;
  orderId: string;
  text: string;
  addedBy: string;
  addedByRole?: string;
  createdAt: string;
}

const DemographicsPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [demographicsData, setDemographicsData] = useState<DemographicsData[]>([]);
  const [filteredData, setFilteredData] = useState<DemographicsData[]>([]);
  const [loading, setLoading] = useState(false);
  const [smartSearchTerm, setSmartSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalKey, setModalKey] = useState(0); // Add this to force Upload component reset
  const [isResubmitModalVisible, setIsResubmitModalVisible] = useState(false);
  const [resubmitRecord, setResubmitRecord] = useState<DemographicsData | null>(null);
  const [resubmitFileState, setResubmitFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    surgeryDate: null,
    enableOnDemand: false,
    uploadError: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [allRecordsCount, setAllRecordsCount] = useState(0);
  const [, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [updatingOnDemand, setUpdatingOnDemand] = useState<string | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [statusStats, setStatusStats] = useState({
    'Pending': 0,
    'In Progress': 0,
    'Accepted': 0,
    'Rejected': 0
  });
  // Initialize selectedStatus from navigation state if available
  const [selectedStatus, setSelectedStatus] = useState<string | null>(() => {
    const navigationState = location.state as { status?: string } | null;
    return navigationState?.status || null;
  });
  
  // Track if we've already processed the initial navigation state
  const [hasProcessedNavigationState, setHasProcessedNavigationState] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [smartSearchField, setSmartSearchField] = useState<string>('all');
  const [deletedRecords, setDeletedRecords] = useState<DeletedRecord[]>([]);
  const [showDeletedRecords, setShowDeletedRecords] = useState(false);
  const [deletedRecordsCount, setDeletedRecordsCount] = useState(0);
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  
  // Notes-related state
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DemographicsData | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    id: true,
    doctorName: true,
    procedureName: true,
    firstName: true,
    middleName: true,
    lastName: true,
    dateOfBirth: true,
    surgeryDate: true,
    uploadedOn: true,
    filename: true,
    onDemand: true,
    status: true,
    actions: true
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'id',
    'doctorName',
    'procedureName',
    'firstName',
    'middleName',
    'lastName',
    'dateOfBirth',
    'surgeryDate',
    'uploadedOn',
    'filename',
    'onDemand',
    'status',
    'actions'
  ]);

  // Table ref for clearing filters and sorting
  const tableRef = useRef<any>(null);

  // Key to force table re-render when clearing filters/sorting
  const [tableKey, setTableKey] = useState(0);


  const [fileState, setFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    surgeryDate: null,
    enableOnDemand: false,
    uploadError: null
  });

  // Fetch deleted records with search/filter support
  const fetchDeletedRecords = async (search: string = '', searchField: string = 'all') => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      if (search) {
        params.append('search', search);
        params.append('searchField', searchField);
      }
      
      const url = `${BACKEND_URL}/deleted${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDeletedRecords(data.data || []);
        setDeletedRecordsCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching deleted records:', error);
    }
  };

  // Fetch demographics data with server-side pagination
  const fetchDemographicsData = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '', showDuplicates: boolean = false) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: 'uploadedOn',
        sortOrder: 'desc'
      });

      if (search) {
        params.append('search', search);
        params.append('searchField', searchField);
      }
      if (status) {
        params.append('status', status);
      }
      if (showDuplicates) {
        params.append('isDuplicate', 'true');
      } else {
        params.append('isDuplicate', 'false');
      }


      const response = await fetch(`${BACKEND_URL}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const demographics = data.data || [];
        const pagination = data.pagination || {};
        const stats = data.stats || {};

        // Update pagination info
        setPaginationInfo(pagination);
        setTotalCount(pagination.totalCount || 0);

        // Update stats from API response (these reflect the filtered data)
        if (stats.statusCounts) {
          setStatusStats(stats.statusCounts);
          setAllRecordsCount(stats.statusCounts['Pending'] + stats.statusCounts['In Progress'] + stats.statusCounts['Accepted'] + stats.statusCounts['Rejected']);
        }
        if (stats.duplicateCount !== undefined) {
          setDuplicateCount(stats.duplicateCount);
        }
        // if (stats.totalCount !== undefined) {
        //   setAllRecordsCount(stats.totalCount-duplicateCount);
        // }

        // Apply client-side sorting for onDemand priority (since server doesn't handle this complex sorting)
        const sortedDemographics = [...demographics].sort((a: DemographicsData, b: DemographicsData) => {
          // First sort by onDemand (true values first)
          if (a.onDemand !== b.onDemand) {
            return b.onDemand ? 1 : -1;
          }
          // Then sort by timestamp (newest first) - this should already be handled by server
          return new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime();
        });

        setDemographicsData(sortedDemographics);
        // setFilteredData will be called automatically by useEffect when demographicsData changes
      } else {
        message.error('Failed to fetch demographics data');
      }
    } catch (error) {
      message.error('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Note: Status statistics are now fetched along with demographics data in fetchDemographicsData
  // This eliminates 3 separate API calls and ensures stats always match the filtered data

  // Notes-related functions
  // Fetch notes for a specific demographics record
  const fetchNotes = async (demographicsId: string) => {
    setNotesLoading(true);
    try {
      const response = await fetch(`/api/notes/${demographicsId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.data || []);
      } else {
        message.error('Failed to fetch notes');
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Error fetching notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  // Add a new note
  const addNote = async (demographicsId: string, text: string) => {
    setAddingNote(true);
    try {
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderId: demographicsId, text })
      });
      if (response.ok) {
        message.success('Note added successfully');
        setNewNoteText('');
        // Refresh notes list
        await fetchNotes(demographicsId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      message.error('Error adding note');
    } finally {
      setAddingNote(false);
    }
  };

  // Delete a note
  const deleteNote = async (noteId: string, demographicsId: string) => {
    try {
      const response = await fetch(`/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        message.success('Note deleted successfully');
        // Refresh notes list
        await fetchNotes(demographicsId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Error deleting note');
    }
  };

  // Handle opening notes modal
  const handleOpenNotesModal = (record: DemographicsData) => {
    setSelectedRecord(record);
    setIsNotesModalVisible(true);
    setNewNoteText('');
    fetchNotes(String(record.id));
  };

  // Handle closing notes modal
  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
    setSelectedRecord(null);
    setNotes([]);
    setNewNoteText('');
  };

  // Handle adding a note
  const handleAddNote = () => {
    if (selectedRecord && newNoteText.trim()) {
      addNote(String(selectedRecord.id), newNoteText.trim());
    }
  };

  // Check if notes should be read-only (currently allowing all statuses to have editable notes)
  const isNotesReadOnly = (_record: DemographicsData) => {
    return false; // Allow notes to be added/edited for all statuses
  };

  // Check if user can delete a note
  const canDeleteNote = (note: Note) => {
    // Check if current user's name matches the note creator's name, or if user is admin
    return user?.name === note.addedBy || user?.role === 'admin';
  };

  // Handle status filter click
  const handleStatusFilterClick = (status: string | null) => {
    console.log('🔄 handleStatusFilterClick: setCurrentPage(1) called with status:', status);
    setSelectedStatus(status);
    setShowDuplicates(false);
    setShowDeletedRecords(false);
    setCurrentPage(1); // Reset to first page
  };

  // Handle duplicate filter click
  const handleDuplicateFilterClick = () => {
    console.log('🔄 handleDuplicateFilterClick: setCurrentPage(1) called');
    setSelectedStatus(null);
    setShowDuplicates(true);
    setShowDeletedRecords(false);
    setCurrentPage(1); // Reset to first page
  };


  // Fetch doctors from database
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.data || []);
      } else {
        message.error('Failed to fetch doctors');
      }
    } catch (error) {
      message.error('Error fetching doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Fetch procedures by doctor
  const fetchProceduresByDoctor = async (doctorId: string) => {
    setLoadingProcedures(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors/${doctorId}/procedures`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableProcedures(data.data || []);
      } else {
        message.error('Failed to fetch procedures');
        setAvailableProcedures([]);
      }
    } catch (error) {
      message.error('Error fetching procedures');
      setAvailableProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  // Handle onDemand status change
  const handleOnDemandChange = async (id: string, newStatus: boolean) => {
    setUpdatingOnDemand(id);
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/on-demand`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          onDemand: newStatus
        }),
      });

      if (response.ok) {
        message.success('On-demand status updated successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data with updated sorting (includes stats)
      } else {
        message.error('Failed to update on-demand status');
      }
    } catch (error) {
      message.error('Error updating on-demand status');
    } finally {
      setUpdatingOnDemand(null);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDoctors(); // Fetch doctors on component mount
    fetchDeletedRecords(smartSearchTerm, smartSearchField); // Fetch deleted records on component mount
    // Note: Stats are now fetched along with demographics data in fetchDemographicsData
  }, []);

  // Handle navigation state ONCE on initial mount
  useEffect(() => {
    // Only process navigation state once when component first mounts
    if (!hasProcessedNavigationState) {
      const navigationState = location.state as { status?: string } | null;
      const statusFromState = navigationState?.status || null;
      
      if (statusFromState) {
        console.log('Processing initial navigation state:', statusFromState);
        setSelectedStatus(statusFromState);
        setShowDuplicates(false);
        setShowDeletedRecords(false);
        setCurrentPage(1);
      }
      
      // Mark as processed so we don't run this again
      setHasProcessedNavigationState(true);
    }
  }, [hasProcessedNavigationState, location.state]); // Run once when component mounts

  // Fetch paginated data when filters or pagination change
  useEffect(() => {
    console.log('useEffect triggered with:', { currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus, showDuplicates });
    // Always fetch both demographics and deleted records to keep counts accurate
    fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates);
    fetchDeletedRecords(smartSearchTerm, smartSearchField);
  }, [currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus, showDuplicates]);

  // Handle click outside to close columns dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the columns dropdown and button
      if (showColumnsDropdown && 
          !target.closest('[data-columns-dropdown]') && 
          !target.closest('[data-columns-button]')) {
        setShowColumnsDropdown(false);
      }
    };

    if (showColumnsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnsDropdown]);

  // Handle smart search
  const handleSmartSearch = (searchTerm: string, selectedField: string = 'all') => {
    console.log('🔄 handleSmartSearch: called with searchTerm:', searchTerm, 'selectedField:', selectedField, 'current smartSearchTerm:', smartSearchTerm);
    
    // Only reset to first page if the search term has actually changed
    const hasSearchTermChanged = searchTerm !== smartSearchTerm;
    const isNewSearch = searchTerm.trim() !== '' && hasSearchTermChanged;
    
    setSmartSearchTerm(searchTerm);
    setSmartSearchField(selectedField);
    
    if (isNewSearch) {
      console.log('🔄 handleSmartSearch: setCurrentPage(1) called because searchTerm changed to:', searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    } else {
      console.log('🔄 handleSmartSearch: NOT resetting page because searchTerm unchanged or empty');
    }
  };

  // Update filteredData when demographicsData changes (for server-side pagination)
  // Apply role-based filtering for insurance verifiers
  useEffect(() => {
    let filtered = [...demographicsData];
    
    // Filter data based on user role
    if (user?.role === 'insurance_verifier') {
      // Insurance verifiers can only see: Pending, In Progress, Approved, Rejected
      // Only Pending items are actionable, others are view-only
      filtered = demographicsData.filter(record => 
        record.status === 'Pending' || 
        record.status === 'In Progress' || 
        record.status === 'Accepted' || 
        record.status === 'Rejected'
      );
    }
    
    setFilteredData(filtered);
  }, [demographicsData, user?.role]);

  // Memoize filter options to prevent unnecessary re-renders
  const doctorFilterOptions = useMemo(() => {
    return Array.from(new Set(demographicsData.map(item => item.doctorName))).map(name => ({
      text: name,
      value: name,
    }));
  }, [demographicsData]);

  const procedureFilterOptions = useMemo(() => {
    return Array.from(new Set(demographicsData.map(item => item.procedureName))).map(name => ({
      text: name,
      value: name,
    }));
  }, [demographicsData]);

  // Handle PDF viewing
  const handleViewPDF = (id: string) => {
    const viewerUrl = `/view-pdf/demographics/${id}`;
    window.open(viewerUrl, '_blank');
  };

  // Handle view insurance file
  const handleViewInsuranceFile = (id: string) => {
    const viewerUrl = `/view-pdf/insurance/${id}`;
    window.open(viewerUrl, '_blank');
  };



     // Handle surgery date change directly
   const handleSurgeryDateChange = async (id: string, newDate: Dayjs | null) => {
     try {
       // Find the current record to get the uploaded date
       const currentRecord = demographicsData.find(item => item.id === id);
       if (!currentRecord) {
         message.error('Record not found');
         return;
       }

       // Validation: Check surgery date against upload date
       if (newDate) {
         const uploadedDate = dayjs(currentRecord.uploadedOn);
         const surgeryDate = dayjs(newDate);

         if (surgeryDate.isBefore(uploadedDate)) {
           // Show warning but allow update to proceed
           message.warning('Surgery date is before the upload date. Please verify this information.');
         }
         // If surgery date >= upload date, no warning needed
       }

       const response = await fetch(`${BACKEND_URL}/${id}`, {
         method: 'PUT',
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify({
           surgeryDate: newDate ? newDate.toISOString() : null
         }),
       });

       if (response.ok) {
         message.success('Surgery date updated successfully');
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data
       } else {
         message.error('Failed to update surgery date');
       }
     } catch (error) {
       message.error('Error updating surgery date');
     }
   };

  // Handle delete patient
  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const responseData = await response.json();
        message.success(responseData.message);
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data (includes stats)
        fetchDeletedRecords(smartSearchTerm, smartSearchField); // Refresh deleted records count
      } else {
        message.error('Failed to delete patient');
      }
    } catch (error) {
      message.error('Error deleting patient');
    }
  };

  // Handle delete with confirmation
  const handleDeleteWithConfirmation = (id: string) => {
    Modal.confirm({
      title: 'Delete Patient Record',
      content: 'This operation cannot be reverted. Are you sure you want to delete this patient record?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => handleDelete(id)
    });
  };

  // Handle show deleted records
  const handleShowDeletedRecords = () => {
    console.log('🔄 handleShowDeletedRecords: setCurrentPage(1) called');
    setShowDeletedRecords(true);
    setSelectedStatus(null);
    setShowDuplicates(false);
    setCurrentPage(1); // Reset to first page
  };

  // Handle back to main view
  const handleBackToMainView = () => {
    console.log('🔄 handleBackToMainView: setCurrentPage(1) called');
    setShowDeletedRecords(false);
    setSelectedStatus(null);
    setShowDuplicates(false);
    setCurrentPage(1); // Reset to first page
  };



  // Handle clear table filters and sorting only
  const handleClearTableFilters = () => {
    // Force table re-render to clear all filters and sorting state completely
    setTableKey(prev => prev + 1);

    message.success('Table filters and sorting cleared');
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = (columnKey: string, checked: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: checked
    }));
  };

  // Column configuration with display names
  const columnConfigMap = {
    id: 'ID',
    doctorName: 'Doctor Name',
    procedureName: 'Procedure',
    firstName: 'First Name',
    middleName: 'Middle Name',
    lastName: 'Last Name',
    dateOfBirth: 'Date of Birth',
    surgeryDate: 'Surgery Date',
    uploadedOn: 'Uploaded On',
    filename: 'Filename',
    onDemand: 'On Demand',
    status: 'Status',
    actions: 'Actions'
  };

  // Get ordered column config based on columnOrder
  const getOrderedColumnConfig = () => {
    return columnOrder.map(key => ({
      key,
      label: columnConfigMap[key as keyof typeof columnConfigMap]
    }));
  };

  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and order columns based on visibility state and column order
  const getVisibleColumns = (originalColumns: ColumnsType<DemographicsData>) => {
    // First filter by visibility
    const visibleColumns = originalColumns.filter(column => {
      const columnKey = column.key as string;
      return columnVisibility[columnKey] !== false;
    });

    // Then sort by column order
    return visibleColumns.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.key as string);
      const indexB = columnOrder.indexOf(b.key as string);
      return indexA - indexB;
    });
  };

  // Handle mark as non-duplicate
  const handleMarkAsNonDuplicate = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/mark-non-duplicate`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        message.success('Record marked as non-duplicate successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data (includes stats)
        fetchDeletedRecords(smartSearchTerm, smartSearchField); // Refresh deleted records count
      } else {
        message.error('Failed to mark record as non-duplicate');
      }
    } catch (error) {
      message.error('Error marking record as non-duplicate');
    }
  };

  // Table columns configuration for deleted records
  const deletedColumns: ColumnsType<DeletedRecord> = [
    {
      title: 'Original ID',
      dataIndex: 'originalId',
      key: 'originalId',
      width: 100,
      sorter: (a, b) => a.originalId - b.originalId,
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 200,
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
    },
    {
      title: 'Procedure',
      dataIndex: 'procedureName',
      key: 'procedureName',
      width: 180,
      sorter: (a, b) => a.procedureName.localeCompare(b.procedureName),
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      width: 120,
      render: (text) => text || '',
    },
    {
      title: 'Middle Name',
      dataIndex: 'middleName',
      key: 'middleName',
      width: 120,
      render: (text) => text || '',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      width: 120,
      render: (text) => text || '',
    },
    {
      title: 'Date of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      width: 120,
      render: (text) => text || '',
    },
    {
      title: 'Surgery Date',
      dataIndex: 'surgeryDate',
      key: 'surgeryDate',
      width: 140,
      render: (date) => date ? new Date(date).toLocaleDateString() : '',
      sorter: (a, b) => new Date(a.surgeryDate).getTime() - new Date(b.surgeryDate).getTime(),
    },
    {
      title: 'Uploaded On',
      dataIndex: 'uploadedOn',
      key: 'uploadedOn',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.uploadedOn).getTime() - new Date(b.uploadedOn).getTime(),
    },
    {
      title: 'Deleted On',
      dataIndex: 'deletedOn',
      key: 'deletedOn',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.deletedOn).getTime() - new Date(b.deletedOn).getTime(),
    },
         {
       title: 'Filename',
       dataIndex: 'filename',
       key: 'filename',
       width: 200,
       ellipsis: true,
       render: (text, record) => (
         <Button
           type="link"
           onClick={() => handleViewPDF(record.originalId.toString())}
           style={{ padding: 0, height: 'auto' }}
         >
           {text}
         </Button>
       ),
     },
  ];

  // Table columns configuration for regular demographics
  const columns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 200,
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      filters: doctorFilterOptions,
      onFilter: (value, record) => record.doctorName === value,
    },
    {
      title: 'Procedure',
      dataIndex: 'procedureName',
      key: 'procedureName',
      width: 180,
      sorter: (a, b) => a.procedureName.localeCompare(b.procedureName),
      filters: procedureFilterOptions,
      onFilter: (value, record) => record.procedureName === value,
    },
         {
              title: 'First Name',
        dataIndex: 'firstName',
        key: 'firstName',
        width: 120,
        render: (text) => text || '',
        sorter: (a, b) => {
          const firstNameA = (a.firstName || '').toLowerCase();
          const firstNameB = (b.firstName || '').toLowerCase();
          return firstNameA.localeCompare(firstNameB);
        },
     },
    {
             title: 'Middle Name',
       dataIndex: 'middleName',
       key: 'middleName',
       width: 120,
       render: (text) => text || '',
    },
                   {
               title: 'Last Name',
         dataIndex: 'lastName',
         key: 'lastName',
         width: 120,
         render: (text) => text || '',
         sorter: (a, b) => {
           const lastNameA = (a.lastName || '').toLowerCase();
           const lastNameB = (b.lastName || '').toLowerCase();
           return lastNameA.localeCompare(lastNameB);
         },
      },
     {
              title: 'Date of Birth',
        dataIndex: 'dateOfBirth',
        key: 'dateOfBirth',
        width: 120,
        render: (text) => text || '',
     },
     {
              title: 'Surgery Date',
        dataIndex: 'surgeryDate',
        key: 'surgeryDate',
        width: 130,
        render: (date, record) => {
          // Disable editing for Accepted, Rejected, and In Progress records
          const isEditable = record.status === 'Pending';
          
          // For insurance verifiers, only Pending records are actionable
          // For manufacturers, no records are actionable (read-only access)
          const isActionable = user?.role === 'insurance_verifier' ? 
            record.status === 'Pending' : 
            user?.role === 'manufacturer' ? 
            false : isEditable;

          if (isActionable) {
            return (
              <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(newDate: Dayjs | null) => handleSurgeryDateChange(record.id, newDate)}
                size="small"
                style={{ width: '100%' }}
              />
            );
          } else {
            // Display as read-only text for non-editable records
            return (
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                {date ? new Date(date).toLocaleDateString() : 'Not set'}
              </span>
            );
          }
        },
        sorter: (a, b) => new Date(a.surgeryDate).getTime() - new Date(b.surgeryDate).getTime(),
     },
    {
      title: 'Uploaded On',
      dataIndex: 'uploadedOn',
      key: 'uploadedOn',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.uploadedOn).getTime() - new Date(b.uploadedOn).getTime(),
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 200,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewPDF(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'On Demand',
      dataIndex: 'onDemand',
      key: 'onDemand',
      width: 120,
      render: (onDemand, record) => {
        // Disable editing for Accepted, Rejected, and In Progress records
        const isEditable = record.status === 'Pending';
        
        // For insurance verifiers, only Pending records are actionable
        // For manufacturers, no records are actionable (read-only access)
        const isActionable = user?.role === 'insurance_verifier' ? 
          record.status === 'Pending' : 
          user?.role === 'manufacturer' ? 
          false : isEditable;

        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Spin
              spinning={updatingOnDemand === record.id}
              indicator={<LoadingOutlined style={{ fontSize: 14 }} />}
            >
              <Switch
                checked={onDemand}
                onChange={(checked) => handleOnDemandChange(record.id, checked)}
                disabled={updatingOnDemand === record.id || !isActionable}
                size="small"
              />
            </Spin>
          </div>
        );
      },
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.onDemand === value,
      // Remove default sorter to maintain our custom sorting
      sorter: false,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const statusConfig = {
          'Pending': { color: '#faad14', icon: <ClockCircleOutlined />, text: 'Pending' },
          'In Progress': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'In Progress' },
          'Accepted': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Accepted' },
          'Rejected': { color: '#ff4d4f', icon: <CloseCircleOutlined />, text: 'Rejected' }
        };

        const config = statusConfig[status as keyof typeof statusConfig];

        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Tag color={config.color} style={{ margin: 0 }}>
              {config.icon}
              <span style={{ marginLeft: '4px' }}>{config.text}</span>
            </Tag>
          </div>
        );
      },
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Accepted', value: 'Accepted' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
         {
       title: 'Actions',
       key: 'actions',
       width: 120,
       render: (_, record) => {
         // Disable editing and deletion for Accepted, Rejected, and In Progress records
         const isEditable = record.status === 'Pending';
         
         // For insurance verifiers, only Pending records are actionable
         // For manufacturers, no records are actionable (read-only access)
         const isActionable = user?.role === 'insurance_verifier' ? 
           record.status === 'Pending' : 
           user?.role === 'manufacturer' ? 
           false : isEditable;

         return (
           <div style={{ display: 'flex', gap: '4px' }}>
             {record.isDuplicate ? (
               <>
                 <Button
                   type="text"
                   size="small"
                   icon={<RotateCcw size={16} />}
                   onClick={() => {
                     Modal.confirm({
                       title: 'Mark as Non-Duplicate',
                       content: 'This operation cannot be reverted. Are you sure you want to mark this record as non-duplicate?',
                       okText: 'Yes',
                       cancelText: 'No',
                       onOk: () => handleMarkAsNonDuplicate(record.id)
                     });
                   }}
                   disabled={!isActionable}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: '#1890ff',
                     opacity: isActionable ? 1 : 0.5
                   }}
                   title="Mark as Non-Duplicate"
                 />
                 {isActionable && (
                   <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Delete Duplicate Record',
                          content: 'This operation cannot be reverted. Are you sure you want to delete this duplicate record?',
                          okText: 'Yes',
                          cancelText: 'No',
                          onOk: () => handleDelete(record.id)
                        });
                      }}
                      style={{ 
                        padding: '4px 8px',
                        minWidth: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Delete Duplicate"
                    />
                 )}

                 {/* Order Notes - Available for duplicate records */}
                 <Button
                   type="text"
                   size="small"
                   icon={<FileTextOutlined />}
                   onClick={() => handleOpenNotesModal(record)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: '#722ed1'
                   }}
                   title="Order Notes"
                 />
               </>
             ) : (
               <>
                 {/* Show delete button only for Pending records */}
                 {isActionable && (
                   <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteWithConfirmation(record.id)}
                      style={{ 
                        padding: '4px 8px',
                        minWidth: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Delete Record"
                    />
                 )}
                 
                 {/* Show view insurance file button for Accepted and Rejected records */}
                 {(record.status === 'Accepted' || record.status === 'Rejected') && (
                   <Button
                     type="text"
                     size="small"
                     icon={<FileTextOutlined />}
                     onClick={() => handleViewInsuranceFile(record.id)}
                     style={{ 
                       padding: '4px 8px',
                       minWidth: '32px',
                       height: '32px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       color: '#1890ff'
                     }}
                     title="View Insurance File"
                   />
                 )}

                 {/* Order Notes - Available for all users */}
                 <Button
                   type="text"
                   size="small"
                   icon={<FileTextOutlined />}
                   onClick={() => handleOpenNotesModal(record)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: '#722ed1'
                   }}
                   title="Order Notes"
                 />
               </>
             )}
           </div>
         );
       },
     },
  ];

  // Columns for rejected records
  const rejectedColumns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Rejected Reason',
      dataIndex: 'rejectedReason',
      key: 'rejectedReason',
      width: 200,
      render: (text) => text || 'No reason provided',
      ellipsis: true,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 80,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Rejected By',
      dataIndex: 'rejectedBy',
      key: 'rejectedBy',
      width: 100,
      render: (text) => text || 'Unknown',
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 100,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewPDF(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="primary"
            size="small"
            icon={<RotateCcw size={16} />}
            onClick={() => handleResubmit(record)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Resubmit Record"
          />
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewInsuranceFile(record.id)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1890ff'
            }}
            title="View Insurance File"
          />
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleOpenNotesModal(record)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#722ed1'
            }}
            title="Order Notes"
          />
        </div>
      ),
    },
  ];

  // Modal form handlers
  const handleFileChange = (info: any) => {
    const file = info.file;
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
      if (allowedTypes.includes(file.type)) {
        setFileState(prev => ({ ...prev, selectedFile: file, uploadStatus: '', uploadError: null }));
      } else {
        message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
        setFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
      }
    }
  };

  const handleDoctorChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedDoctor: value, selectedProcedure: '', uploadError: null }));
    // Fetch procedures for the selected doctor
    if (value) {
      fetchProceduresByDoctor(value);
    } else {
      setAvailableProcedures([]);
    }
  };

  const handleProcedureChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedProcedure: value, uploadError: null }));
  };

  const handleToggleChange = (checked: boolean) => {
    setFileState(prev => ({ ...prev, enableOnDemand: checked, uploadError: null }));
  };

  const handleModalSubmit = async () => {
    if (!fileState.selectedFile) {
      message.error('Please select a file first.');
      return;
    }

    if (!fileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!fileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    if (!fileState.surgeryDate) {
      message.error('Please select a surgery date.');
      return;
    }

    setFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctorObj = doctors.find(d => d.employeeId === fileState.selectedDoctor);
    const doctorName = selectedDoctorObj ? selectedDoctorObj.name : fileState.selectedDoctor;

    const formData = new FormData();
    formData.append('patientDetails', fileState.selectedFile);
    formData.append('doctor', doctorName); // Send doctor name for backward compatibility
    formData.append('doctorId', fileState.selectedDoctor); // Send doctor ID for future use
    formData.append('procedure', fileState.selectedProcedure);
    formData.append('surgeryDate', fileState.surgeryDate.format('YYYY-MM-DD'));
    formData.append('enableOnDemand', fileState.enableOnDemand.toString());

    try {
      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'POST',
        credentials: 'include', // Send HTTP-only auth cookie (HIPAA-compliant)
        body: formData,
      });

             if (response.ok) {
         let responseData;
         try {
           responseData = await response.json();
         } catch (parseError) {
           // If response is not JSON, treat it as plain text success
           responseData = { message: 'File uploaded successfully', isDuplicate: false };
         }

         if (responseData.isDuplicate || responseData.warning) {
           // Show warning for duplicates or surgery date issues
           message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
         } else {
           message.success(responseData.message || 'File uploaded successfully!');
         }

         setFileState({
           selectedFile: null,
           isUploading: false,
           uploadStatus: '',
           selectedDoctor: '',
           selectedProcedure: '',
           surgeryDate: null,
           enableOnDemand: false,
           uploadError: null
         });
         setIsModalVisible(false);
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data (includes stats)
               } else {
          // Handle backend errors properly
          let errorMessage = 'Upload failed. Please try again.';
          let validationErrors: string[] = [];

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;

            // Check if there are validation errors
            if (errorData.validationErrors && Array.isArray(errorData.validationErrors)) {
              validationErrors = errorData.validationErrors;
            }
          } catch (parseError) {
            // If error response is not JSON, try to get text
            try {
              const errorText = await response.text();
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              // If all else fails, use default message
              console.error('Could not parse error response:', textError);
            }
          }

          // Display validation errors if they exist
          if (validationErrors.length > 0) {
            const validationMessage = `File validation failed:\n${validationErrors.map(error => `• ${error}`).join('\n')}`;
            message.error({
              content: validationMessage,
              duration: 8, // Show longer for validation errors
              style: { whiteSpace: 'pre-line' }
            });
            errorMessage = validationMessage;
          } else {
            message.error(errorMessage);
          }

          // Set error state for display in modal
          setFileState(prev => ({ ...prev, uploadError: errorMessage }));
        }
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        message.error(`Upload failed: ${error.message}`);
      } else {
        message.error('Upload failed. Please check your connection.');
      }
    } finally {
      setFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    clearFileState();
  };

  const clearFileState = () => {
    setFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      surgeryDate: null,
      enableOnDemand: false,
      uploadError: null
    });
  };

  const clearResubmitFileState = () => {
    setResubmitFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      surgeryDate: null,
      enableOnDemand: false,
      uploadError: null
    });
  };

  // Handle resubmit button click
  const handleResubmit = (record: DemographicsData) => {
    setResubmitRecord(record);
    setResubmitFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: record.doctorName,
      selectedProcedure: record.procedureName,
      surgeryDate: record.surgeryDate,
      enableOnDemand: record.onDemand,
      uploadError: null
    });
    setIsResubmitModalVisible(true);
  };

  // Handle resubmit modal submit
  const handleResubmitSubmit = async () => {
    if (!resubmitRecord) return;

    if (!resubmitFileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!resubmitFileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    setResubmitFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctorObj = doctors.find(d => d.employeeId === resubmitFileState.selectedDoctor);
    const doctorName = selectedDoctorObj ? selectedDoctorObj.name : resubmitFileState.selectedDoctor;

    const formData = new FormData();

    // Only append file if a new one is selected
    if (resubmitFileState.selectedFile) {
      formData.append('patientDetails', resubmitFileState.selectedFile);
    }

    formData.append('doctor', doctorName);
    formData.append('doctorId', resubmitFileState.selectedDoctor);
    formData.append('procedure', resubmitFileState.selectedProcedure);
    formData.append('enableOnDemand', resubmitFileState.enableOnDemand.toString());
    formData.append('keepExistingFile', (!resubmitFileState.selectedFile).toString());

    try {
      const response = await fetch(`${BACKEND_URL}/resubmit/${resubmitRecord.id}`, {
        method: 'PUT',
        credentials: 'include', // Send HTTP-only auth cookie (HIPAA-compliant)
        body: formData,
      });

      if (response.ok) {
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          responseData = { message: 'Record resubmitted successfully', isDuplicate: false };
        }

        if (responseData.isDuplicate || responseData.warning) {
          message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
        } else {
          message.success('Record resubmitted successfully!');
        }

        clearResubmitFileState();
        setIsResubmitModalVisible(false);
        setResubmitRecord(null);
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '', showDuplicates); // Refresh data (includes stats)
      } else {
        let errorMessage = 'Resubmit failed. Please try again.';
        let validationErrors: string[] = [];

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          validationErrors = errorData.errors || [];
        } catch (parseError) {
          // If response is not JSON, use the status text
          errorMessage = response.statusText || errorMessage;
        }

        if (validationErrors.length > 0) {
          message.error(
            <div>
              <div>{errorMessage}</div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Resubmit error:', error);
      message.error('Network error. Please check your connection and try again.');
    } finally {
      setResubmitFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle resubmit modal cancel
  const handleResubmitCancel = () => {
    setIsResubmitModalVisible(false);
    clearResubmitFileState();
    setResubmitRecord(null);
  };

  const handleModalOpen = (e?: React.MouseEvent) => {
    console.log('Add Demographics button clicked - Chrome debug'); // Debug log
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    clearFileState(); // Clear any previous file state when opening modal
    setModalKey(prev => prev + 1); // Increment key to force Upload component reset
    setIsModalVisible(true);
  };

  return (
    <div className="dashboard-page">
             {/* First Container - Search and Add Button (15% height) */}
       <div className="search-container">
         <div className="search-content" style={{ position: 'relative', zIndex: 1 }}>
          <div className="search-inputs" style={{ pointerEvents: 'auto', zIndex: 5, flex: 1 }}>
            <DemographicsSmartSearch
              onSearch={handleSmartSearch}
              style={{ marginBottom: 16, pointerEvents: 'auto' }}
            />
            

          </div>
           <div style={{ 
             position: 'relative', 
             zIndex: 15, 
             marginLeft: '24px',
             flexShrink: 0
           }}>
             <Button
               type="primary"
               icon={<PlusOutlined />}
               onClick={handleModalOpen}
               onMouseDown={(e) => e.preventDefault()}
               size="large"
               style={{ 
                 position: 'relative',
                 zIndex: 20,
                 pointerEvents: 'auto',
                 backgroundColor: '#00a3e0 ',
                 borderColor: '#00a3e0 '
               }}
               data-testid="add-demographics-btn"
             >
               Add Demographics
             </Button>
           </div>
         </div>
       </div>

      {/* Status Statistics Container */}
      <div className="status-stats-container" style={{ 
        padding: '16px', 
        backgroundColor: '#f5f5f5', 
        marginBottom: '16px',
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>
          {/* All Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick(null)}
            style={{
              cursor: 'pointer',
              border: selectedStatus === null && !showDuplicates && !showDeletedRecords ? '2px solid #00a3e0 ' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === null && !showDuplicates && !showDeletedRecords ? '#e6f7ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#722ed1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <UserOutlined />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>All</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{allRecordsCount} records</div>
              </div>
            </div>
          </Card>

          {/* Pending Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Pending')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Pending' ? '2px solid #faad14' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Pending' ? '#fffbe6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileClock size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Pending</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Pending']} records</div>
              </div>
            </div>
          </Card>

          {/* In Progress Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('In Progress')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'In Progress' ? '2px solid #1890ff' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'In Progress' ? '#e6f7ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <Loader size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>In Progress</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['In Progress']} records</div>
              </div>
            </div>
          </Card>

          {/* Accepted Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Accepted')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Accepted' ? '2px solid #52c41a' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Accepted' ? '#f6ffed' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileCheck size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Accepted</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Accepted']} records</div>
              </div>
            </div>
          </Card>

          {/* Rejected Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Rejected')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Rejected' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Rejected' ? '#fff2f0' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#ff4d4f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CircleX size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Rejected</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Rejected']} records</div>
              </div>
            </div>
          </Card>

          {/* Duplicates Box */}
          <Card
            hoverable
            onClick={handleDuplicateFilterClick}
            style={{
              cursor: 'pointer',
              border: showDuplicates ? '2px solid #fa8c16' : '1px solid #d9d9d9',
              backgroundColor: showDuplicates ? '#fff7e6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#fa8c16',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <Files size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Duplicates</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{duplicateCount} records</div>
              </div>
            </div>
          </Card>

          {/* Deleted Records Box */}
          <Card
            hoverable
            onClick={handleShowDeletedRecords}
            style={{
              cursor: 'pointer',
              border: showDeletedRecords ? '2px solid #722ed1' : '1px solid #d9d9d9',
              backgroundColor: showDeletedRecords ? '#f9f0ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#722ed1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <DeleteOutlined style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Deleted</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{deletedRecordsCount} records</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

                           {/* Second Container - Table (remaining height) */}
        <div className="table-container1">
          {/* Action Buttons Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {showDeletedRecords && (
                <Button 
                  icon={<RotateCcw size={16} />} 
                  onClick={handleBackToMainView}
                  type="primary"
                  ghost
                >
                  Back to Main View
                </Button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
              {/* Columns Button */}
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                size="middle"
                type="default"
                title="Column settings"
                data-columns-button
              >
                Columns
              </Button>

              {/* Columns Dropdown Menu */}
              {showColumnsDropdown && (
                <div
                  data-columns-dropdown
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px',
                    minWidth: '220px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    marginBottom: '12px',
                    color: '#262626',
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '8px'
                  }}>
                    Column Visibility
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {getOrderedColumnConfig().map((column) => (
                          <SortableItem
                            key={column.key}
                            id={column.key}
                            label={column.label}
                            checked={columnVisibility[column.key]}
                            onToggle={handleColumnVisibilityChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Clear Table Filters Button */}
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearTableFilters}
                size="middle"
                type="default"
                title="Clear table column filters and sorting"
              >
                Clear Table Filters
              </Button>
            </div>
          </div>

                     <Card title={showDeletedRecords ? "Deleted Records" : "Demographics Data"} className="table-card">
              <Table
                key={`${tableKey}-${currentPage}-${pageSize}`}
                ref={tableRef}
                columns={showDeletedRecords ? (deletedColumns as any) : (selectedStatus === 'Rejected' ? (rejectedColumns as any) : (getVisibleColumns(columns) as any))}
                dataSource={showDeletedRecords ? deletedRecords as any : filteredData}
                rowKey={showDeletedRecords ? "_id" : "id"}
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: showDeletedRecords ? deletedRecords.length : totalCount,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, size) => {
                    console.log("page selected is", page);
                    setCurrentPage(page);
                    setPageSize(size || 10);
                    // fetchDemographicsData will be called automatically via useEffect
                  },
                }}
                scroll={{ x: showDeletedRecords ? 1200 : 1400 }}
                size="middle"
                getPopupContainer={() => document.body}
              />
          </Card>
       </div>

      {/* Upload Modal */}
      <Modal
        title="Add New Demographics"
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        width={600}
        confirmLoading={fileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={fileState.selectedDoctor}
              onChange={handleDoctorChange}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={fileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={fileState.selectedProcedure}
              onChange={handleProcedureChange}
              style={{ width: '100%' }}
              disabled={!fileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Surgery Date: <span style={{ color: 'red' }}>*</span></label>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Select surgery date"
              format="YYYY-MM-DD"
              value={fileState.surgeryDate}
              onChange={(date) => setFileState(prev => ({ ...prev, surgeryDate: date }))}
              disabledDate={(current) => {
                // Disable past dates
                return current && current < dayjs().startOf('day');
              }}
            />
          </div>

          <div className="form-item">
            <label>Upload File:</label>
            <Upload
              key={modalKey}
              beforeUpload={() => false}
              onChange={handleFileChange}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </div>

                     <div className="form-item">
             <Switch
               checked={fileState.enableOnDemand}
               onChange={handleToggleChange}
             />
             <span style={{ marginLeft: '8px' }}>Enable on demand</span>
           </div>

                     {/* Error Display */}
           {fileState.uploadError && (
             <div className="form-item" style={{ marginTop: 16 }}>
               <div style={{ 
                 padding: '12px 16px', 
                 backgroundColor: '#fff2f0', 
                 border: '1px solid #ffccc7', 
                 borderRadius: '6px',
                 color: '#cf1322',
                 whiteSpace: 'pre-line'
               }}>
                 <strong>Upload Error:</strong><br />
                 {fileState.uploadError}
               </div>
             </div>
           )}
        </div>
      </Modal>

      {/* Resubmit Modal */}
      <Modal
        title="Resubmit Demographics Record"
        open={isResubmitModalVisible}
        onOk={handleResubmitSubmit}
        onCancel={handleResubmitCancel}
        width={600}
        confirmLoading={resubmitFileState.isUploading}
        okText="Resubmit"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            marginBottom: '16px',
            color: '#389e0d'
          }}>
            <strong>Resubmit Confirmation:</strong><br />
            You are about to resubmit record ID: {resubmitRecord?.id}. This will update the existing record with new data. You can optionally upload a new file or keep the current one. Please review and confirm your changes.
          </div>

          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={resubmitFileState.selectedDoctor}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedDoctor: value }))}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={resubmitFileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={resubmitFileState.selectedProcedure}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedProcedure: value }))}
              style={{ width: '100%' }}
              disabled={!resubmitFileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Current File:</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <span>{resubmitRecord?.filename}</span>
              </div>
              <Button
                type="link"
                size="small"
                onClick={() => handleViewPDF(resubmitRecord?.id || '')}
                style={{ padding: '0 4px' }}
              >
                View
              </Button>
            </div>
          </div>

          <div className="form-item">
            <label>Upload New File (Optional):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
                  if (file.type && allowedTypes.includes(file.type)) {
                    setResubmitFileState(prev => ({ ...prev, selectedFile: file as File, uploadError: null }));
                  } else {
                    message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
                    setResubmitFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
                  }
                }
              }}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose New File</Button>
            </Upload>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Leave empty to keep the current file
            </div>
          </div>

          <div className="form-item">
            <Switch
              checked={resubmitFileState.enableOnDemand}
              onChange={(checked) => setResubmitFileState(prev => ({ ...prev, enableOnDemand: checked }))}
            />
            <span style={{ marginLeft: '8px' }}>Enable on demand</span>
          </div>

          {/* Error Display */}
          {resubmitFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '6px',
                color: '#cf1322',
                whiteSpace: 'pre-line'
              }}>
                <strong>Upload Error:</strong><br />
                {resubmitFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        title={`Order Notes for #${selectedRecord?.id || ''}`}
        open={isNotesModalVisible}
        onCancel={handleCloseNotesModal}
        footer={null}
        width={600}
        centered
      >
        {selectedRecord && !isNotesReadOnly(selectedRecord) && (
          <div style={{ marginBottom: '16px' }}>
            <Input.TextArea
              placeholder="Add a new note..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={3}
              disabled={addingNote}
              style={{ marginBottom: '8px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                type="primary"
                onClick={handleAddNote}
                disabled={!newNoteText.trim() || addingNote}
                loading={addingNote}
                size="small"
              >
                Save Note
              </Button>
              <Button
                onClick={() => setNewNoteText('')}
                disabled={addingNote}
                size="small"
              >
                Clear
              </Button>
            </div>
            {addingNote && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Spin size="small" /> Adding note...
              </div>
            )}
          </div>
        )}

        {selectedRecord && isNotesReadOnly(selectedRecord) && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            color: '#389e0d'
          }}>
            <strong>Read-Only Mode:</strong> This record is completed or cancelled. Notes can only be viewed, not modified.
          </div>
        )}

        {/* Role Information Banner */}
        <div style={{ 
          marginBottom: '16px', 
          padding: '10px', 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px',
          fontSize: '12px',
          color: '#0050b3'
        }}>
          <strong>Note:</strong> {user?.role === 'admin' ? 'Admins can delete any note' : 'You can only delete notes you created'}
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notesLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
            </div>
          ) : notes.length === 0 ? (
            <Empty 
              description="No notes yet" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={notes}
              renderItem={(note) => (
                <List.Item
                  actions={[
                    // Only show delete button if user can delete this note and record is not read-only
                    selectedRecord && !isNotesReadOnly(selectedRecord) && canDeleteNote(note) && (
                      <Button
                        key="delete"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Delete Note',
                            content: 'Are you sure you want to delete this note?',
                            okText: 'Yes',
                            cancelText: 'No',
                            onOk: () => deleteNote(note._id, String(selectedRecord?.id))
                          });
                        }}
                        size="small"
                      />
                    )
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        {note.text}
                      </div>
                    }
                    description={
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Added by {note.addedBy}
                        {note.addedByRole && (
                          <Tag 
                            color={
                              note.addedByRole === 'admin' ? 'red' :
                              note.addedByRole === 'practitioner' ? 'blue' :
                              note.addedByRole === 'manufacturer' ? 'green' :
                              note.addedByRole === 'insurance' ? 'orange' :
                              'default'
                            }
                            style={{ marginLeft: '8px', fontSize: '11px' }}
                          >
                            {note.addedByRole.charAt(0).toUpperCase() + note.addedByRole.slice(1)}
                          </Tag>
                        )}
                        {' • '}{new Date(note.createdAt).toLocaleString()}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DemographicsPage;
